import { computed, inject, Injectable, signal } from '@angular/core';
import { ApiErrorResponse, ErrorHandlerV2Service, SortDirection } from '@gea/digital-ui-lib';
import { isEqual } from 'lodash';
import { catchError, first, of } from 'rxjs';

import { CustomerOverviewDialogService, OrganizationsApiService } from '@shared/services';
import { MachinesStore } from '@shared/state/machines';
import {
  Organization,
  OrganizationPermission,
  OrganizationsStateModel,
  Permission,
  SortOrganizationsParams,
} from '@shared/models';
import { MembershipsStore } from '../memberships';
import { FaultsStore } from '../faults';
import { SystemHealthStore } from '../system-health';
import { TroubleshootingStore } from '../troubleshooting';
import { FaultRecommendationsStore } from '../fault-recommendations';
import { LicensingStore } from '../licensing';
import { OrganizationsComparator, SORT_COLUMN } from './organizations.comparator';

export const SELECTED_ORGANIZATION_KEY = 'selectedOrganization';

const INITIAL_STATE: OrganizationsStateModel = {};

@Injectable({ providedIn: 'root' })
export class OrganizationsStore {
  private readonly troubleshootingStore = inject(TroubleshootingStore);
  private readonly machinesStore = inject(MachinesStore);
  private readonly faultsStore = inject(FaultsStore);
  private readonly faultRecommendationsStore = inject(FaultRecommendationsStore);
  private readonly membershipsStore = inject(MembershipsStore);
  private readonly licensingStore = inject(LicensingStore);
  private readonly systemHealthStore = inject(SystemHealthStore);
  private readonly organizationsApi = inject(OrganizationsApiService);
  private readonly errorHandler = inject(ErrorHandlerV2Service);
  private readonly customerOverviewDialogService = inject(CustomerOverviewDialogService);

  private readonly state = signal<OrganizationsStateModel>(INITIAL_STATE);

  readonly isLoading = computed<boolean | undefined>(() => this.state().isLoading);
  readonly organizationId = computed<number | undefined>(() => this.state().organizationId);
  readonly organizations = computed<Organization[] | undefined>(() => this.state().organizations, { equal: isEqual });
  readonly filteredOrganizations = computed<Organization[] | undefined>(() => this.state().filteredOrganizations, {
    equal: isEqual,
  });
  readonly selected = computed<OrganizationPermission | undefined>(() => this.state().selected, { equal: isEqual });
  readonly permission = computed<Permission | undefined>(() => this.state().selected?.permission);

  /**
   * Loads the organizations of the current user. The previous list is kept when the response is unchanged.
   * On error the list is replaced with an empty array after the error has been reported.
   */
  fetchOrganizations(): void {
    this.patch({ isLoading: true });

    this.organizationsApi
      .fetchOrganizations()
      .pipe(
        catchError((error: ApiErrorResponse) => {
          if (error) {
            this.errorHandler.handleError(error);
          }
          return of([]);
        }),
        first()
      )
      .subscribe((organizations: Organization[]) => {
        if (!isEqual(this.state().organizations, organizations)) {
          this.patch({ organizations });
        }

        this.patch({ isLoading: false });
      });
  }

  /**
   * Selects the organization, resolves the permission from the memberships when none is given,
   * and resets the dependent states.
   */
  setSelectedOrganization(organization: Organization, permission?: Permission): void {
    sessionStorage.setItem(SELECTED_ORGANIZATION_KEY, JSON.stringify(organization.id));

    this.patch({
      organizationId: organization.id,
      selected: {
        organization,
        permission:
          permission ??
          (this.membershipsStore.memberships()?.find((m) => m.organization.name === organization.name)?.role.name as Permission),
      },
    });

    this.machinesStore.fetchMachines();

    this.sortOrganizations({
      columnName: SORT_COLUMN.FAULTS,
      sortOrder: SortDirection.DESCENDING,
      organizationId: organization.id,
    });

    this.faultsStore.resetSelectedFaultAnalysisState();
    this.troubleshootingStore.resetParams();
    this.faultRecommendationsStore.resetState();

    this.systemHealthStore.reset();
    this.licensingStore.reset();

    this.customerOverviewDialogService.closeDialog();
  }

  sortOrganizations({ columnName, sortOrder }: SortOrganizationsParams): void {
    const filteredOrganizations = [...(this.state().filteredOrganizations ?? [])];
    filteredOrganizations.sort(OrganizationsComparator.by(columnName, sortOrder));

    this.patch({ filteredOrganizations });
  }

  filterOrganizations(filterString: string): void {
    const customerList = this.state().organizations;

    if (!customerList) {
      return;
    }

    const filteredOrganizations = customerList.filter(
      (customer: Organization) => customer.name.toLowerCase().indexOf(filterString.toLowerCase()) !== -1
    );

    this.patch({ filteredOrganizations });
  }

  private patch(partial: Partial<OrganizationsStateModel>): void {
    this.state.update((current) => ({ ...current, ...partial }));
  }
}
